import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, PackageCheck } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canReceive, canQCComplete, useAuth } from "@/lib/auth";

// V1.4 — Merged Receive + QC (unit-based) page. Loads all units of one
// purchase and lets an authorised operator (WAREHOUSE / QC_ENGINEER /
// ADMIN / SUPER_ADMIN / RECEPTION) complete each unit independently:
// enter Serial (required) + Barcode (optional) + QC checklist → click
// "Complete QC" → that ONE unit advances to PENDING_BATTERY_DETAILS while
// the others stay Pending. Nothing else in the app is touched.

export default function UnitReceiveQcPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [purchase, setPurchase] = useState(null);
  const [template, setTemplate] = useState(null);
  const [rowState, setRowState] = useState({}); // { [passport_id]: {serial, barcode, checklist, busy, error, done} }
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/purchases/${id}`),
        api.get("/qc/checklist-template"),
      ]);
      setPurchase(p.data);
      setTemplate(t.data);
    } catch (e) {
      setError(errorToMessage(e));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const canWork = canReceive(user) || canQCComplete(user);

  const setField = (pid, k, v) => setRowState((s) => ({
    ...s, [pid]: { ...(s[pid] || {}), [k]: v },
  }));

  const setChecklistField = (pid, code, key, v) => setRowState((s) => {
    const row = s[pid] || {};
    const cl = { ...(row.checklist || {}) };
    cl[code] = { ...(cl[code] || {}), [key]: v };
    return { ...s, [pid]: { ...row, checklist: cl } };
  });

  // A checklist item is "complete" per the same rules the QcDetailPage uses:
  // PASS/FAIL items need result, PERCENT/NUMBER items need a value >= 0,
  // GRADE items need grade.
  const itemComplete = (item, ans) => {
    if (!ans) return false;
    const k = (item.kind || "").toUpperCase();
    if (k === "PERCENT" || k === "NUMBER") return ans.value != null && ans.value !== "";
    if (k === "GRADE") return !!ans.grade;
    return !!ans.result;
  };

  const rowReady = (pid) => {
    const r = rowState[pid] || {};
    if (!r.serial || !r.serial.trim()) return false;
    const cl = r.checklist || {};
    return (template?.items || []).every((it) => !it.required || itemComplete(it, cl[it.code]));
  };

  const submit = async (pid) => {
    if (!rowReady(pid)) return;
    setField(pid, "busy", true);
    setField(pid, "error", null);
    try {
      const r = rowState[pid];
      await api.post(`/purchases/${id}/units/${pid}/receive-and-qc`, {
        manufacturer_serial: r.serial.trim(),
        internal_barcode: (r.barcode || "").trim() || null,
        checklist: r.checklist || {},
      });
      setField(pid, "done", true);
      setField(pid, "busy", false);
      await load();
    } catch (e) {
      setField(pid, "error", errorToMessage(e, "Complete QC failed"));
      setField(pid, "busy", false);
    }
  };

  if (error) return <div className="p-6 text-rose-700">{error}</div>;
  if (!purchase || !template) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;
  if (!canWork) return <div className="p-6 text-slate-600">You don&apos;t have permission to receive / QC units on this purchase.</div>;

  // Only show units that are still in a pre-battery state.
  const pending = (purchase.units || []).filter((u) => u.passport_id
    && ["PENDING_RECEIVING", "PENDING_SERIAL_ENTRY", "QC_PENDING"].includes(u.passport_status));
  const done = (purchase.units || []).filter((u) =>
    u.passport_id && u.passport_status && u.passport_status !== "PENDING_RECEIVING"
    && u.passport_status !== "PENDING_SERIAL_ENTRY" && u.passport_status !== "QC_PENDING");

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Link to={`/admin/purchases/${id}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm">
          <ArrowLeft size={14}/> Back
        </Link>
        <h1 className="text-lg font-semibold text-slate-900">Receive &amp; QC · Unit-based</h1>
        <span className="text-xs text-slate-500">Purchase #{purchase.purchase_no || purchase.id?.slice(0, 8)} · {pending.length} pending · {done.length} completed</span>
      </div>

      {pending.length === 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 text-sm">
          <CheckCircle2 size={14} className="inline mr-1"/> All units have completed Receive &amp; QC. Battery testing is now available.
        </div>
      )}

      <ul className="space-y-3">
        {pending.map((u, idx) => {
          const pid = u.passport_id;
          const r = rowState[pid] || {};
          const ready = rowReady(pid);
          return (
            <li key={pid} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`unit-card-${pid}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-sm font-medium text-slate-900">
                  Unit {idx + 1} <span className="text-slate-400">· {u.brand || "—"} {u.model || ""}</span>
                </div>
                <span className="inline-flex rounded-full bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 text-[11px]">Pending QC</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">Serial Number <span className="text-rose-500">*</span></span>
                  <input data-testid={`unit-${pid}-serial`}
                    value={r.serial || ""}
                    onChange={(e) => setField(pid, "serial", e.target.value)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
                    placeholder="Enter manufacturer serial (required)" />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">Internal Barcode <span className="text-slate-400 text-[11px]">(optional)</span></span>
                  <input data-testid={`unit-${pid}-barcode`}
                    value={r.barcode || ""}
                    onChange={(e) => setField(pid, "barcode", e.target.value)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
                    placeholder="Optional" />
                </label>
              </div>

              {r.serial && r.serial.trim() && (
                <div className="mt-2 border-t border-slate-100 pt-3">
                  <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">QC Checklist</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`unit-${pid}-checklist`}>
                    {(template.items || []).map((it) => (
                      <ChecklistItem key={it.code} item={it}
                        value={(r.checklist || {})[it.code]}
                        onChange={(k, v) => setChecklistField(pid, it.code, k, v)} />
                    ))}
                  </div>
                </div>
              )}

              {r.error && <div className="mt-3 text-xs text-rose-700">{r.error}</div>}

              <div className="mt-3 flex justify-end">
                <button data-testid={`unit-${pid}-complete-qc`}
                  onClick={() => submit(pid)}
                  disabled={!ready || r.busy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-40">
                  {r.busy ? <Loader2 size={14} className="animate-spin"/> : <PackageCheck size={14}/>}
                  Complete QC
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {done.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <summary className="cursor-pointer text-slate-600">Already completed / advanced ({done.length})</summary>
          <ul className="mt-2 space-y-1 text-xs text-slate-500">
            {done.map((u) => (
              <li key={u.passport_id || u.id} className="flex justify-between">
                <span className="font-mono">{u.manufacturer_serial || "—"} · {u.internal_barcode || "—"}</span>
                <span>{u.passport_status}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ChecklistItem({ item, value, onChange }) {
  const kind = (item.kind || "").toUpperCase();
  const label = <span className="block text-xs font-medium text-slate-700 mb-1">
    {item.label}{item.required && <span className="ml-1 text-rose-500">*</span>}
  </span>;
  const testId = `chk-${item.code}`;
  if (kind === "PERCENT" || kind === "NUMBER") {
    return (
      <label className="block">
        {label}
        <input type="number" data-testid={testId}
          value={value?.value ?? ""} min={0}
          onChange={(e) => onChange("value", e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
      </label>
    );
  }
  if (kind === "GRADE") {
    return (
      <label className="block">
        {label}
        <select data-testid={testId} value={value?.grade || ""} onChange={(e) => onChange("grade", e.target.value)}
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm">
          <option value="">—</option>
          {["A", "B", "C", "D"].map((g) => <option key={g} value={g}>Grade {g}</option>)}
        </select>
      </label>
    );
  }
  // PASS/FAIL default
  return (
    <div>
      {label}
      <div className="flex items-center gap-3 text-xs">
        {["PASS", "FAIL", "NA"].map((r) => (
          <label key={r} className="inline-flex items-center gap-1">
            <input type="radio" name={`${item.code}`} data-testid={`${testId}-${r}`}
              checked={value?.result === r}
              onChange={() => onChange("result", r)} />
            {r}
          </label>
        ))}
      </div>
    </div>
  );
}
